import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { authHeaders, http } from './http.js';

async function listProcesses(getToken, companyId, includeDisabled) {
    const headers = await authHeaders(getToken);
    const { data } = await http.get(`/company/${companyId}/process`, {
        headers,
        params: { includeDisabled },
    });
    return data;
}

async function createProcess(getToken, companyId, input) {
    const headers = await authHeaders(getToken);
    const { data } = await http.post(`/company/${companyId}/process`, input, { headers });
    return data;
}

async function setEnabled(getToken, companyId, processId, enabled) {
    const headers = await authHeaders(getToken);
    const action = enabled ? 'enable' : 'disable';
    const { data } = await http.post(`/company/${companyId}/process/${processId}/${action}`, null, { headers });
    return data;
}

export function useProcesses(companyId, includeDisabled) {
    const { getToken } = useAuth();
    return useQuery({
        queryKey: ['processes', companyId, includeDisabled],
        queryFn: () => listProcesses(getToken, companyId, includeDisabled),
        enabled: Boolean(companyId),
    });
}

export function useCreateProcess(companyId, onSuccess) {
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => createProcess(getToken, companyId, input),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['processes', companyId] });
            onSuccess?.(data);
        },
    });
}

export function useSetProcessEnabled(companyId) {
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ processId, enabled }) => setEnabled(getToken, companyId, processId, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['processes', companyId] });
        },
    });
}